import React, { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { a2aAdminService } from '../../a2a/services/a2aAdminService';
import { isAdmin } from '../../lib/permissions';

// MainPage Component - Admin version with enhanced features
const MainPage = ({
  currentUser,
  attendanceData,
  paymentData,
  newsData,
  schoolEvents,
  onTabChange,
  calculateAttendanceRate,
  getAttendanceStatus,
  formatCurrency,
  formatDate
}) => {
  // Admin-specific stats
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    totalClasses: 0,
    activeUsers: 0,
    systemHealth: 'good'
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch admin statistics
  useEffect(() => {
    if (isAdmin(currentUser)) {
      fetchAdminStats();
      fetchRecentActivities();
      fetchSystemAlerts();
    }
  }, [currentUser]);

  const fetchAdminStats = async () => {
    setLoading(true);
    try {
      const tenantId = currentUser?.tenant_id || 'tn_01';
      const result = await a2aAdminService.getSystemAnalytics({
        tenant_id: tenantId
      });
      if (result.success) {
        setAdminStats({
          totalUsers: result.data?.total_users || 0,
          totalClasses: 0, // TODO: Fetch from backend
          activeUsers: result.data?.active_users || 0,
          systemHealth: result.data?.error_rate < 1 ? 'good' : 'warning'
        });
      }
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    // TODO: Implement fetch recent activities
    setRecentActivities([
      { type: 'user', action: '新用戶註冊', user: '張三', time: '2小時前' },
      { type: 'class', action: '班級更新', class: 'S1A', time: '5小時前' },
      { type: 'system', action: '系統備份完成', time: '1天前' }
    ]);
  };

  const fetchSystemAlerts = async () => {
    // TODO: Implement fetch system alerts
    setSystemAlerts([
      { type: 'info', message: '系統運行正常', time: '剛剛' },
      { type: 'warning', message: '有3個待處理的用戶申請', time: '1小時前' }
    ]);
  };

  // Defensive programming - ensure arrays exist before filtering
  const safeAttendanceData = Array.isArray(attendanceData) ? attendanceData : [];
  const safePaymentData = Array.isArray(paymentData) ? paymentData : [];
  const safeNewsData = Array.isArray(newsData) ? newsData : [];
  const safeSchoolEvents = Array.isArray(schoolEvents) ? schoolEvents : [];

  // Get recent attendance data
  const recentAttendance = safeAttendanceData.slice(0, 5);
  
  // Get recent payment data
  const recentPayments = safePaymentData.slice(0, 5);
  
  // Get recent news
  const recentNews = safeNewsData.slice(0, 5);
  
  // Get upcoming events
  const upcomingEvents = safeSchoolEvents.slice(0, 3);

  // Determine if user is admin
  const adminUser = isAdmin(currentUser);

  return (
    <div style={{maxWidth: '1200px', margin: '0 auto'}}>
      {/* Header Section */}
      <div style={{marginBottom: '32px'}}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          歡迎回來，{currentUser?.name || (adminUser ? '管理員' : '用戶')}
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          今天是 {new Date().toLocaleDateString('zh-TW')}，讓我們開始今天的工作
        </p>
      </div>

      {/* Admin Quick Stats */}
      {adminUser && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard icon="👥" label="用戶總數" value={adminStats.totalUsers} />
          <StatCard icon="🏫" label="班級總數" value={adminStats.totalClasses} />
          <StatCard icon="✅" label="活躍用戶" value={adminStats.activeUsers} />
          <StatCard icon="💚" label="系統狀態" value={adminStats.systemHealth === 'good' ? '正常' : '警告'} />
        </div>
      )}

      {/* Admin Quick Actions */}
      {adminUser && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <button 
            onClick={() => onTabChange('users')}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            👥 用戶管理
          </button>
          <button 
            onClick={() => onTabChange('classes')}
            style={{
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            🏫 班級管理
          </button>
          <button 
            onClick={() => onTabChange('system-settings')}
            style={{
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            ⚙️ 系統設定
          </button>
          <button 
            onClick={() => onTabChange('analytics')}
            style={{
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            📊 分析報表
          </button>
        </div>
      )}

      {/* System Alerts (Admin Only) */}
      {adminUser && systemAlerts.length > 0 && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '32px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#92400e',
            marginBottom: '12px'
          }}>
            ⚠️ 系統提醒
          </h3>
          {systemAlerts.map((alert, index) => (
            <div key={index} style={{
              padding: '8px 0',
              borderBottom: index < systemAlerts.length - 1 ? '1px solid #fde68a' : 'none'
            }}>
              <span style={{color: '#92400e'}}>{alert.message}</span>
              <span style={{color: '#a16207', fontSize: '12px', marginLeft: '8px'}}>{alert.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#10b981',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{color: 'white', fontSize: '24px'}}>📊</span>
            </div>
            <div>
              <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>考勤紀錄</div>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
                {safeAttendanceData.length}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#3b82f6',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{color: 'white', fontSize: '24px'}}>💰</span>
            </div>
            <div>
              <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>繳費紀錄</div>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
                {safePaymentData.length}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#f59e0b',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{color: 'white', fontSize: '24px'}}>📰</span>
            </div>
            <div>
              <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>學校宣佈</div>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
                {safeNewsData.length}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#8b5cf6',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{color: 'white', fontSize: '24px'}}>🎉</span>
            </div>
            <div>
              <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>學校活動</div>
              <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
                {safeSchoolEvents.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <button 
          onClick={() => onTabChange('attendance')}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📊 考勤紀錄
        </button>
        
        <button 
          onClick={() => onTabChange('payment')}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          💰 繳費紀錄
        </button>
        
        <button 
          onClick={() => onTabChange('news')}
          style={{
            backgroundColor: '#f59e0b',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📰 學校宣佈
        </button>
        
        <button 
          onClick={() => onTabChange('school-events')}
          style={{
            backgroundColor: '#8b5cf6',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          🎉 學校活動
        </button>
        
        <button 
          onClick={() => onTabChange('academic-calendar')}
          style={{
            backgroundColor: '#06b6d4',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📅 校曆表
        </button>
        
        <button 
          onClick={() => onTabChange('teacher-directory')}
          style={{
            backgroundColor: '#ec4899',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👥 教師通訊錄
        </button>

        {/* New Information Buttons */}
        <button 
          onClick={() => onTabChange('teacher-info')}
          style={{
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👨‍🏫 教師資訊
        </button>
        
        <button 
          onClick={() => onTabChange('student-info')}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👨‍🎓 學生資訊
        </button>
        
        <button 
          onClick={() => onTabChange('class-info')}
          style={{
            backgroundColor: '#f59e0b',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          🏫 班級資訊
        </button>

        {/* Staff Button */}
        <button 
          onClick={() => onTabChange('staff')}
          style={{
            backgroundColor: '#8b5cf6',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👷 職員
        </button>

        {/* Library Button */}
        <button 
          onClick={() => onTabChange('library')}
          style={{
            backgroundColor: '#059669',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📚 圖書館
        </button>

              {/* Enhanced App Store Button */}
              <button
                onClick={() => onTabChange('app-store')}
                style={{
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🚀 Enhanced AppStore
              </button>

              {/* AI AppStore Button */}
              <button
                onClick={() => onTabChange('app-store')}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🛍️ AI AppStore
              </button>

            {/* Developer Portal Button */}
            <button
              onClick={() => onTabChange('developer-portal')}
              style={{
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                padding: '16px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              👨‍💻 Developer Portal
            </button>

            {/* Admin Review System Button */}
            <button
              onClick={() => onTabChange('admin-review')}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '16px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              🔍 Admin Review
            </button>

            {/* Billing Dashboard Button */}
            <button
              onClick={() => onTabChange('billing-dashboard')}
              style={{
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                padding: '16px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              💰 Billing Dashboard
            </button>
      </div>

      {/* Recent Activities (Admin Enhanced) */}
      {adminUser && recentActivities.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            最近活動
          </h3>
          {recentActivities.map((activity, index) => (
            <div key={index} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: index < recentActivities.length - 1 ? '1px solid #e5e7eb' : 'none'
            }}>
              <span style={{color: '#374151'}}>
                {activity.action} {activity.user && `- ${activity.user}`} {activity.class && `(${activity.class})`}
              </span>
              <span style={{color: '#6b7280', fontSize: '12px'}}>{activity.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Recent Attendance */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            最近考勤紀錄
          </h3>
          {recentAttendance.length > 0 ? (
            <div>
              {recentAttendance.map((record, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index < recentAttendance.length - 1 ? '1px solid #e5e7eb' : 'none'
                }}>
                  <span style={{color: '#374151'}}>
                    {record.studentName || `學生${index + 1}`}
                  </span>
                  <span style={{
                    color: record.status === 'present' ? '#10b981' : '#ef4444',
                    fontWeight: '500'
                  }}>
                    {getAttendanceStatus ? getAttendanceStatus(record.status) : record.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{color: '#6b7280', fontStyle: 'italic'}}>暫無考勤紀錄</p>
          )}
        </div>

        {/* Recent News */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            最新學校宣佈
          </h3>
          {recentNews.length > 0 ? (
            <div>
              {recentNews.map((news, index) => (
                <div key={index} style={{
                  padding: '8px 0',
                  borderBottom: index < recentNews.length - 1 ? '1px solid #e5e7eb' : 'none'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1f2937',
                    marginBottom: '4px'
                  }}>
                    {news.title || `公告${index + 1}`}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    {formatDate ? formatDate(news.date) : news.date}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{color: '#6b7280', fontStyle: 'italic'}}>暫無最新公告</p>
          )}
        </div>
      </div>

      {/* Upcoming Events */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        padding: '20px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          marginBottom: '16px'
        }}>
          即將舉行的活動
        </h3>
        {upcomingEvents.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {upcomingEvents.map((event, index) => (
              <div key={index} style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>
                  {event.title || `活動${index + 1}`}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  {formatDate ? formatDate(event.date) : event.date}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  {event.description || '活動描述'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>暫無即將舉行的活動</p>
        )}
      </div>
    </div>
  );
};

// StatCard Component for Admin Stats
const StatCard = ({ icon, label, value }) => {
  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: '#f3f4f6',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px'
        }}>
          {icon}
        </div>
        <div>
          <div style={{fontSize: '14px', color: '#6b7280', marginBottom: '4px'}}>{label}</div>
          <div style={{fontSize: '24px', fontWeight: '700', color: '#1f2937'}}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
